import { config } from '../config/environment';
import { getIsFirstCanvassCreated, setIsFirstCanvassCreated, getIsTutorialComp, setIsTutorialComp } from './globalState';
import { logger } from './logger';

// Import getAccessToken for authentication
const getAccessToken = async () => {
  // First try to use the global token getter set up by useAuthenticatedApi hook
  if (typeof window !== 'undefined' && window.getAccessToken) {
    try {
      // Determine the correct scope based on environment
      const scope = config.isProduction 
        ? `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/clients-prod.access_as_user`
        : `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/clients-dev.access_as_user`;
      
      return await window.getAccessToken(scope);
    } catch (error) {
      logger.warn('Global getAccessToken failed, falling back to direct MSAL:', error);
    }
  }
  
  // Fallback to direct MSAL access for non-React contexts
  const { msalInstance } = await import('../config/msalConfig');
  
  // Determine the correct scope based on environment
  const environment = process.env.NODE_ENV || 'development';
  const scope = environment === 'production' 
    ? `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/clients-prod.access_as_user`
    : `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/clients-dev.access_as_user`;
  
  const accounts = msalInstance.getAllAccounts();
  
  if (accounts.length === 0) {
    throw new Error('No authenticated user found. Please log in first.');
  }
  
  const account = accounts[0];
  
  try {
    // Try to get token silently first with clients-specific scope
    const response = await msalInstance.acquireTokenSilent({
      scopes: [scope],
      account: account,
    });
    return response.accessToken;
  } catch (error) {
    // If silent acquisition fails, try interactive method
    if (error.errorCode === 'consent_required' || 
        error.errorCode === 'interaction_required' || 
        error.errorCode === 'login_required') {
      try {
        const response = await msalInstance.acquireTokenPopup({
          scopes: [scope],
        });
        return response.accessToken;
      } catch (interactiveError) {
        throw new Error(`Failed to acquire access token: ${interactiveError.message}`);
      }
    }
    throw new Error(`Failed to acquire access token: ${error.message}`);
  }
};

const CLIENTS_API_BASE = config.clientsApiBase || '';

export const fetchClientById = async (options = {}) => {
  const { clientId, returnFull = false, apiBase } = options;
  if (!clientId) throw new Error('fetchClientById: clientId is required');

  const base = apiBase || CLIENTS_API_BASE;
  if (!base) {
    throw new Error(
      'Clients API base URL not set. Provide REACT_APP_CLIENTS_API_BASE or pass options.apiBase.'
    );
  }

  const url = `${base}clients/${encodeURIComponent(clientId)}`;
  
  // Get access token for authentication
  const token = await getAccessToken();
  
  const response = await fetch(url, { 
    method: 'GET', 
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    } 
  });
  const result = await response.json();
  if (!response.ok || result.success === false) {
    throw new Error(result.error || result.message || 'Failed to fetch client');
  }
  if (returnFull) return result;
  return result.client ?? null;
};

export const updateClient = async (options = {}) => {
  const { clientId, body = {}, apiBase, returnFull = false } = options;
  if (!clientId) throw new Error('updateClient: clientId is required');

  const base = apiBase || CLIENTS_API_BASE;
  if (!base) {
    throw new Error(
      'Clients API base URL not set. Provide REACT_APP_CLIENTS_API_BASE or pass options.apiBase.'
    );
  }

  const url = `${base}clients/${encodeURIComponent(clientId)}`;
  const payload = { clientId, ...body };
  
  // Get access token for authentication
  const token = await getAccessToken();
  
  const response = await fetch(url, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(payload),
  });
  const result = await response.json();
  if (!response.ok || result.success === false) {
    throw new Error(result.error || result.message || 'Failed to update client');
  }
  if (returnFull) return result;
  return result.client ?? null;
};

export const createClient = async (options = {}) => {
  const { clientId, body = {}, apiBase, returnFull = false } = options;
  if (!body.clientId) throw new Error('createClient: body.clientId is required');

  const base = apiBase || CLIENTS_API_BASE;
  if (!base) {
    throw new Error(
      'Clients API base URL not set. Provide REACT_APP_CLIENTS_API_BASE or pass options.apiBase.'
    );
  }

  const url = `${base}clients/create/${encodeURIComponent(clientId)}`;
  const payload = { ...body };
  
  // Get access token for authentication
  const token = await getAccessToken();
  
  const response = await fetch(url, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(payload),
  });
  const result = await response.json();
  if (!response.ok || result.success === false) {
    throw new Error(result.error || result.message || 'Failed to create client');
  }
  if (returnFull) return result;
  return result.client ?? null;
};

// Convenience helper: fetch client and ensure tutorial flag is set
export const ensureTutorialCompleted = async (options = {}) => {
  const { clientId, apiBase } = options;
  if (!clientId) throw new Error('ensureTutorialCompleted: clientId is required');

  const cached = getIsTutorialComp();
  if (typeof cached !== 'undefined') {
    return { isTutorialComp: cached };
  }

  const client = await fetchClientById({ clientId, apiBase });
  const value = Boolean(client && client.isTutorialComp === true);
  setIsTutorialComp(value);
  return { isTutorialComp: value };
};

export const ensureTutorialCompletedCached = async (options = {}) => {
  const { clientId, apiBase } = options;
  if (!clientId) throw new Error('ensureTutorialCompletedCached: clientId is required');
  const cached = getIsTutorialComp();
  if (typeof cached !== 'undefined') {
    return { isTutorialComp: cached };
  }
  return ensureTutorialCompleted({ clientId });
};

export const ensureFirstCanvassCreated = async (options = {}) => {
  const { clientId, apiBase } = options;
  if (!clientId) throw new Error('ensureFirstCanvassCreated: clientId is required');

  // Cached global value short-circuit
  const cached = getIsFirstCanvassCreated();
  if (typeof cached !== 'undefined') {
    return { isFirstCanvassCreated: cached };
  }

  const client = await fetchClientById({ clientId, apiBase });
  const value = Boolean(client && client.isFirstCanvassComp === true);
  setIsFirstCanvassCreated(value);
  return { isFirstCanvassCreated: value };
};

export const ensureFirstCanvassCreatedCached = async (options = {}) => {
  const { clientId, apiBase } = options;
  const cached = getIsFirstCanvassCreated();
  if (typeof cached !== 'undefined') return { isFirstCanvassCreated: cached };
  return ensureFirstCanvassCreated({ clientId, apiBase });
};

export const updateFirstCanvassCreated = async (options = {}) => {
  const { clientId, apiBase } = options;
  if (!clientId) throw new Error('updateFirstCanvassCreated: clientId is required');

  const updated = await updateClient({ clientId, apiBase, body: { isFirstCanvassComp: true } });
  setIsFirstCanvassCreated(true);
  return { client: updated || null, isFirstCanvassCreated: true };
}

export const deleteClient = async (options = {}) => {
  const { clientId, returnFull = false, apiBase } = options;
  if (!clientId) throw new Error('deleteClient: clientId is required');

  const base = apiBase || CLIENTS_API_BASE;
  if (!base) {
    throw new Error(
      'Clients API base URL not set. Provide REACT_APP_CLIENTS_API_BASE or pass options.apiBase.'
    );
  }

  const url = `${base}clients/${encodeURIComponent(clientId)}`;
  
  // Get access token for authentication
  const token = await getAccessToken();
  
  const response = await fetch(url, { 
    method: 'DELETE', 
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    } 
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok || result.success === false) {
    throw new Error(result.error || result.message || 'Failed to delete client');
  }
  if (returnFull) return result;
  return { success: true, message: result.message };
};

export const getAllClients = async (options = {}) => {
  const { page = 1, pageSize = 20, isUser = false, apiBase } = options;

  // Build the query string
  const params = new URLSearchParams({
    page: String(Math.max(1, page)),
    pageSize: String(Math.max(1, pageSize)),
    isUser: String(isUser),
  });

  // Construct the full URL to the Azure Function endpoint
  // Adjust baseUrl as needed for your environment (local vs deployed)
  const base = apiBase || CLIENTS_API_BASE;
  if (!base) {
    throw new Error(
      'Clients API base URL not set. Provide REACT_APP_CLIENTS_API_BASE or pass options.apiBase.'
    );
  }

  const url = `${base}clients?${params.toString()}`;

  try {
    // Get access token for authentication
    const token = await getAccessToken();
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'API returned success: false');
    }

    // Return the paginated result shape
    return {
      clients: data.clients || [],
      page: data.page,
      pageSize: data.pageSize,
      totalCount: data.totalCount,
      totalPages: data.totalPages,
      message: data.message,
    };
  } catch (error) {
    logger.error('getAllClients fetch error:', error);
    // Re-throw so callers can handle it, or you could return an error object
    throw error;
  }
};
